import Heading from "components/Typography/Heading"
import styled from "styled-components"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import Button from "components/EvtKit/button/Button"
import Input from "components/EvtKit/input/Input"
import { passwordsMatch, validatePassword } from "utils/auth/authHelper"
import { INVALID_PASSWORD, PASSWORDS_DONT_MATCH } from "constants/responses"
import { useMutation } from "react-query"
import ApiAccounts from "api/ApiAccounts"
import { useAppDispatch } from "reducers/store"
import { showToast } from "reducers/toast/toast-actions"

const ChangePasswordContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1056px;
  width: fit-content;
  gap: 40px;
  background-color: ${(props) => props.theme.palette.white};
  padding: 24px;
  border-radius: 10px;
  box-shadow: 0px 2px 6px 0px #0000001a;
`

const PasswordContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const CurrentPasswordContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 414px;
  width: 100%;
  gap: 16px;
`

const NewPasswordContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 414px;
  width: 100%;
  gap: 16px;
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`

const ContainerInput = styled.div`
  margin: 0 12px;
`

interface UpdatePasswordProps {
  onGoBack: () => void
  onSuccess: () => void
}

export const UpdatePassword: React.FC<UpdatePasswordProps> = (props) => {
  const { onGoBack, onSuccess } = props

  const appDispatch = useAppDispatch()

  const [inputValue, setInputValue] = useState({
    currentPassword: "",
    newPassword: "",
    passwordConfirm: ""
  })

  const [error, setError] = useState({
    currentPassword: "",
    newPassword: "",
    passwordConfirm: ""
  })

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target

    setInputValue((prevState) => ({
      ...prevState,
      [name]: value
    }))
  }, [])

  const checkErrors = () => {
    const hasCurrentPasswordError = inputValue.currentPassword.length < 0
    const hasPasswordError = inputValue.newPassword.length > 0 && !validatePassword(inputValue.newPassword)
    const hasPasswordConfirmError =
      inputValue.passwordConfirm.length > 0 && !passwordsMatch(inputValue.passwordConfirm, inputValue.newPassword)

    setError({
      currentPassword: hasCurrentPasswordError ? "Campo obrigatório" : "",
      newPassword: hasPasswordError ? INVALID_PASSWORD : "",
      passwordConfirm: hasPasswordConfirmError ? PASSWORDS_DONT_MATCH : ""
    })
  }

  useEffect(() => {
    checkErrors()
  }, [inputValue])

  const hasErrors = useMemo(() => {
    const formStateEntries = Object.values(inputValue)
    const fieldErrorsEntries = Object.values(error)

    return formStateEntries.some((entry) => !entry) || fieldErrorsEntries.some((entry) => entry)
  }, [inputValue, error])

  const mutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      await new ApiAccounts().updateProfilePassword(data)
    },
    onSuccess: () => {
      appDispatch(showToast("Os dados foram salvos com sucesso!"))
      onSuccess()
    },
    onError: () => {
      appDispatch(showToast("Não foi possível salvar os dados. Tente novamente.", "error"))
    },
    retry: 0
  })

  const handleSaveClick = useCallback(() => {
    mutation.mutate({ currentPassword: inputValue.currentPassword, newPassword: inputValue.newPassword })
  }, [mutation, inputValue])

  return (
    <ChangePasswordContainer>
      <Heading variant="h5">Alterar senha</Heading>
      <PasswordContainer>
        <CurrentPasswordContainer>
          <Heading variant="h6">Senha Atual</Heading>
          <ContainerInput>
            <Input
              id="currentPassword"
              type="password"
              name="currentPassword"
              onChange={handleChange}
              label="Digite sua senha atual"
              value={inputValue.currentPassword}
              error={error.currentPassword}
            />
          </ContainerInput>
        </CurrentPasswordContainer>
        <NewPasswordContainer>
          <Heading variant="h6">Criar nova senha</Heading>
          <ContainerInput>
            <Input
              id="newPassword"
              type="password"
              name="newPassword"
              onChange={handleChange}
              label="Digite sua nova senha"
              value={inputValue.newPassword}
              error={error.newPassword}
            />
          </ContainerInput>
          <ContainerInput>
            <Input
              id="passwordConfirm"
              type="password"
              name="passwordConfirm"
              onChange={handleChange}
              label="Confirme sua nova senha"
              value={inputValue.passwordConfirm}
              error={error.passwordConfirm}
              sameValue
            />
          </ContainerInput>
        </NewPasswordContainer>
      </PasswordContainer>
      <ButtonContainer>
        <Button disabled={hasErrors || mutation.isLoading} onClick={handleSaveClick} loading={mutation.isLoading}>
          Salvar
        </Button>
        <Button className="secondary" onClick={onGoBack}>
          Cancelar
        </Button>
      </ButtonContainer>
    </ChangePasswordContainer>
  )
}
